/**
 * Room layout: a herringbone Wang tile set, after Sean Barrett.
 *
 * WHY NOT A SQUARE GRID: with square rooms there is a seam that runs the full
 * width and height of the world, and players read it instantly — "oh, this is
 * the grid." Noita's team hit exactly this and moved to herringbone for exactly
 * this reason. Herringbone tiles are interlocking brick-shaped pieces in two
 * orientations; no continuous seam can form because no tile boundary ever lines
 * up with the next one across more than one tile.
 *
 * THE LATTICE. Work in *room units* of RU x RU cells. A horizontal tile is
 * 2x1 units, a vertical tile is 1x2 units. The herringbone tessellation is
 * generated by the lattice A=(3,1), B=(-1,1) with, per lattice point t:
 *     H tile covering units  t, t+(1,0)
 *     V tile covering units  t+(2,0), t+(2,1)
 * det|A B| = 4 = the four units those two tiles cover, so it tiles the plane
 * exactly once. The inverse is the reason this is usable in a streaming
 * generator: the four covered offsets (0,0),(1,0),(2,0),(2,1) have sums
 * 0,1,2,3 — all distinct mod 4 — so
 *
 *     k = (rx + ry) mod 4
 *
 * decodes any unit to its tile in O(1) with no search, no state, and no
 * dependence on generation order. That is what lets a chunk generate its slice
 * of a tile without ever having seen the neighbouring chunk.
 *
 * EDGE MATCHING. Classic Wang tiling picks tiles to match already-placed
 * neighbours, which needs backtracking and a placement order. We invert it: the
 * *edge* is the primitive. Each unit edge's connection type is a pure hash of
 * the edge's own coordinates, so two tiles sharing an edge always agree without
 * communicating. A tile slot then selects from the biome's variant set filtered
 * to those variants that accept the edge signature it was dealt. Same
 * constraint satisfaction, zero coordination.
 *
 * The tile layer governs MACRO layout only. Cave noise still carves intra-tile
 * detail (worldgen.ts) — the tiles decide where places are, the noise decides
 * what they look like.
 *
 * AUTHORED TILES. A slot may be filled by a *drawing* instead of by
 * `buildTile`'s shape vocabulary — `src/world/tiles/*.pxm`, one file per room,
 * loaded below. `docs/19` §6 is what makes that legal rather than ambitious:
 * *"a pixel scene is exactly one wang tile — the tile is the room"*, settled
 * with Noita's own asset names, and their changelog defines a `static_tile`
 * biome as *"a wang biome without randomization, consisting of one large tile"*.
 * The tile is the room either way; randomisation only chooses which room.
 *
 * Two properties above are what make it cheap, and `tests/tileMask.test.ts`
 * spends both rather than citing them. **Edge matching is derived** — an edge
 * is a hash of its own coordinates and nothing is chosen to fit a neighbour —
 * so an authored room needs no variant per signature and no backtracking; it
 * replaces the *body* and `edgeWalls` draws the six exterior edges over it
 * afterwards, unchanged. And the **three-way classification already existed**:
 * `OPEN` is air, `SOLID` is the rock around here, `SHELL`/`PLATFORM`/`GATE` are
 * authored material. Issue #143 made `SOLID` mean one rock per region instead
 * of a different rock every few cells; a `.pxm` drawn in those classes carries
 * no material at all, so one drawing is native rock in every biome it lands in.
 */

import { Rng, hash2 } from '../core/rng';
import { CHUNK_SHIFT, CHUNK_SIZE } from '../sim/world';
import { parseShapeMask, type DeferredClass, type LegendPool, type ShapeMaskDoc } from '../sim/shapeMask';
import { kindGrid } from './tiles/tileMask';
import type { BiomeDef } from './biomes';
import { parsePinDoc, type PinDoc } from './worldmapPins';

/**
 * **The authored tiles: the directory, inlined as string literals at build time.**
 *
 * This was a static `?raw` per file and an array below naming each one, exactly
 * as `rigidShapes.ts` pulled in `crate.pxm` and `pixelBody.ts` pulled in
 * `humanoid.plan`. That costs nothing at runtime and has one defect, which is
 * the whole reason it is a glob now: **a tile the editor creates is not in the
 * game until somebody edits this file.** `pixel tile new` writes a `.pxm`,
 * `pixel tile edit` draws into it, and then the room is nowhere — the generator
 * has never heard of it, `pixel tile show` refuses it as a document the build
 * does not hold, and the only way to finish the job is a TypeScript edit that
 * has nothing to do with drawing.
 *
 * `import.meta.glob` is the same `?raw` mechanism one level up: Vite expands the
 * pattern at build time and inlines every match as a string literal, so there is
 * still no fetch, no asset directory and no new dependency. The precedent is in
 * the tree — `tests/sourceScan.ts` globs all of `src/`, and `tests/shapeMask.ts`
 * and `tests/bodyPlanText.ts` already glob these very directories.
 *
 * **The bundle cost, stated rather than assumed:** `?raw` inlines *one file* and
 * a glob inlines *the directory*. That is a real difference and it is the right
 * one here, because a `.pxm` under `src/world/tiles/` has exactly one purpose —
 * being a room — so a file in this directory that the bundle does not want is a
 * file that should not be in this directory. Today that is 24 KB of grid across
 * two rooms; the same two the hand-written list named.
 *
 * A tile carries *classes* rather than materials, so unlike a rigid shape it has
 * no ordering hazard against `registerMaterials()` at all — there is nothing in
 * it for the material table to answer.
 */
const TILE_TEXT: Record<string, string> = import.meta.glob('./tiles/*.pxm', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/**
 * The pin document's source text, inlined the same `?raw` way `crate.pxm` and
 * every other single-file authored document is — issue #182. One file
 * rather than a glob because there is one document (see `worldmapPins.ts`'s
 * header, "one document or one per world mode").
 */
const WORLDMAP_TEXT: string = (
  import.meta.glob('./worldmap.pxm', { query: '?raw', import: 'default', eager: true }) as Record<string, string>
)['./worldmap.pxm'];

/**
 * A pin document's source text, for the in-game editor — the same reason
 * `tileTextByPath` exists next to `TILE_TEXT`: no filesystem to read from.
 */
export function worldmapPinsText(): string {
  return WORLDMAP_TEXT;
}

/**
 * A tile's source text by its repo-relative path (`src/world/tiles/x.pxm`) —
 * the spelling `tilesFrom` puts in the registry and the picker sends back.
 * For the in-game editor, which has no filesystem to read the document from;
 * a lookup function rather than the table itself so the glob keys stay this
 * module's own business.
 */
export function tileTextByPath(rel: string): string | undefined {
  return TILE_TEXT[rel.replace(/^src\/world\//, './')];
}

/** Room unit edge, in cells. Two units per chunk axis at RU=64. */
export const RU = 64;
export const RU_SHIFT = 6;

/**
 * What a generation pass writes per cell before materials are chosen.
 *
 * **Each member's own comment is the sentence an author reads.** `pixel … edit`'s
 * help panel resolves every `@class` through `kindForClass` and then prints the
 * docblock of the `Kind` it lands on, so these are not notes to the next
 * maintainer alone — they are the in-editor description of what painting with
 * that swatch *does*. `tests/pixelTools.test.ts` requires every class to reach
 * one, which is why the three below have a line each as well as the shared
 * paragraph above them.
 */
export const Kind = {
  /** Rock: whatever the host biome is made of here. `@host` paints this. */
  SOLID: 0,
  /** Air. The `.` character, and a cave mouth is drawn by *removing* rock. */
  OPEN: 1,
  /** Structural boundary — becomes the biome shell material + Flag.STATIC. */
  SHELL: 2,
  /** ADAMANT_SEAL. Honest gating: only the matching ability opens it. */
  GATE: 3,
  /**
   * One-way floor — becomes the biome's `palette.platform` (catwalk or grate).
   *
   * A ledge you can land on, jump up through and drop off is a different room
   * than a ledge that is a wall from below, and until this existed every ledge
   * in the game was the second one. It is a separate `Kind` rather than a
   * dressing because worldgen has to resolve it against the palette, and it is
   * NOT solid to the cell sim (docs/10 §2.3) — sand, water and gas pour
   * straight through, which is most of the point.
   */
  PLATFORM: 4,
  /**
   * **The three below exist only so that an authored solid is not all one
   * material.** No procedural pass writes them; `src/world/tiles/*.pxm` is their
   * only writer, and that is the point rather than an accident.
   *
   * `docs/19` §8.3a measured the ceiling: an authored solid had exactly one
   * class, `SHELL`, which resolves to `palette.shell`, which
   * `tests/paletteStructure.test.ts` records at a 5 % gate with `chlorine_fen`
   * at **4.951 %**. Every drawn room spent the same 0.05 points, so the
   * placement rate was set by a palette slot rather than by the drawing, and
   * `docs/19` §8.3's 2-7 % authored-area band was unaffordable at 0.78 %.
   *
   * **What makes them affordable is that each names a slot the biome already
   * spends elsewhere.** `deposits` lays `palette.fill`, `glowVeins` lays
   * `palette.glow`, and the ore pass in `materialise` lays an accent — all three
   * are in the window before a room is drawn, so a room adds no material to it,
   * only a *shape*. That is `docs/19` §3.2's finding stated as a mechanism: the
   * budget goes into masses inside a room instead of scatter across a screen.
   * `docs/24` §12.6 measures the claim against `regionOccupancy`, the instrument
   * written to catch a slot behaving like `platform`.
   */
  /** The biome's accent — an ore seam. Reach for it to band a wall. */
  VEIN: 5,
  /** The biome's `palette.glow` — light in the mass. Reach for it sparingly. */
  GLOW: 6,
  /** The biome's `palette.fill` — soft bulk, the stuff a deposit is made of. */
  FILL: 7,
} as const;
export type KindId = (typeof Kind)[keyof typeof Kind];

export const EdgeType = {
  WALL: 0,
  /** Narrow passage. Passable. */
  DOOR: 1,
  /** Wide passage. Passable. */
  OPEN: 2,
} as const;
export type EdgeTypeId = (typeof EdgeType)[keyof typeof EdgeType];

export function edgePassable(e: number): boolean {
  return e !== EdgeType.WALL;
}

// --------------------------------------------------------------- the lattice

export const TileOrient = { H: 0, V: 1 } as const;
export type TileOrientId = (typeof TileOrient)[keyof typeof TileOrient];

export interface TileRef {
  /** Top-left unit of the tile. */
  ux: number;
  uy: number;
  orient: TileOrientId;
  /** Which half of the tile the queried unit was: 0 = first, 1 = second. */
  half: number;
}

function mod4(v: number): number {
  return ((v % 4) + 4) % 4;
}

/**
 * Decode a room unit to its herringbone tile. Writes into `out` so the hot
 * path allocates nothing.
 */
export function decodeTile(rx: number, ry: number, out: TileRef): TileRef {
  const k = mod4(rx + ry);
  switch (k) {
    case 0: // west half of a horizontal tile
      out.ux = rx;
      out.uy = ry;
      out.orient = TileOrient.H;
      out.half = 0;
      break;
    case 1: // east half
      out.ux = rx - 1;
      out.uy = ry;
      out.orient = TileOrient.H;
      out.half = 1;
      break;
    case 2: // north half of a vertical tile
      out.ux = rx;
      out.uy = ry;
      out.orient = TileOrient.V;
      out.half = 0;
      break;
    default: // 3 — south half
      out.ux = rx;
      out.uy = ry - 1;
      out.orient = TileOrient.V;
      out.half = 1;
      break;
  }
  return out;
}

export function makeTileRef(): TileRef {
  return { ux: 0, uy: 0, orient: TileOrient.H, half: 0 };
}

// ------------------------------------------------------------- the edge field

/**
 * **How enclosed a biome's room lattice is — 0 dissolved, 1 built.** Issue #57.
 *
 * #57's title is *"twelve biomes, one shape"*, and its decided plan asks for the
 * **room/corridor mix** to be driven per biome *"so a vault reads as built and a
 * fen reads as dissolved"*. This is that lever, and the edge field is where it
 * belongs, because the edge field is literally what the mix is: an OPEN edge
 * merges two room units into one space, a DOOR edge leaves a 2-cell partition
 * the corridor punches through, and a WALL edge is a 3-cell wall the router
 * refuses to cross at all. Three numbers decide a biome's whole macro grain and
 * all three were module constants shared by twelve biomes.
 *
 * The other three shape levers are already per-biome and none of them has room
 * left: `caveAnisoFor` takes `verticality` (#63), `shelfPitchFor` takes it too
 * and is capped at 54 by the jump height, and `megaScaleFor` takes
 * `roomSizeBias` and is at the top of its measured range at 2.1. The edge field
 * is the one shape input in the generator that was still global.
 *
 * **THE CONNECTIVITY GUARANTEE IS UNTOUCHED, BY CONSTRUCTION.** The forced
 * sublattice — every horizontal edge on an even unit row, every vertical edge
 * on an even unit column, and the tile interiors — returns DOOR or OPEN on both
 * branches at every enclosure, so `edgePassable` is true for exactly the same
 * set of edges whatever a biome declares. Enclosure moves the DOOR/OPEN split
 * *inside* the forced set and the WALL/DOOR/OPEN split outside it. That is why
 * this is safe where varying the router itself was not: `tests/setpiece.test.ts`
 * lost two anchor pairs' redundancy the last time the router was made
 * per-biome, and `structure.ts`'s proof is a statement about the forced set.
 *
 * **IT IS A SECOND READER FOR `terrain.caveDensity`, NOT A THIRTEENTH KNOB** —
 * the same rule `megaScaleFor` was added under. `caveDensity` says how much of a
 * biome's volume the *noise* carves; enclosure says how much of the room lattice
 * the *architecture* seals. A place whose space is noise-made should not also be
 * a warren of sealed cells: it should be dissolved. So the map is inverted, and
 * it lands where the fiction already is —
 *
 *     verdigris_spire   0.38 -> 1.00   relic constructs, the most built
 *     cathedral_of_salt 0.40 -> 0.89
 *     emberworks        0.42 -> 0.78
 *     the_long_hour     0.43 -> 0.72
 *     coldforge         0.44 -> 0.67
 *     sunken_vault      0.46 -> 0.56
 *     nanite_reef       0.48 -> 0.44
 *     rotwood_reach     0.50 -> 0.33
 *     meatworks         0.50 -> 0.33
 *     chlorine_fen      0.52 -> 0.22   the fen, dissolved
 *     cinder_march      0.54 -> 0.11
 *     hollow_choir      0.56 -> 0.00
 *
 * — a cathedral and a foundry at the built end, a fen and an erasure at the
 * dissolved end, from a field authored years before this and for another reason.
 *
 * **AND IT SHIPS INERT.** `ENCLOSURE_STRENGTH` is zero, because the lever was
 * swept and it is worth 0.01 of the separation statistic #57 is judged on —
 * the measurement is in the block over the amplitudes, and it is the finding
 * this lever produced rather than a failure to tune it. The dial stays because
 * a null result nobody can re-run is a null result nobody believes; it is the
 * shape `tests/worldVoids.test.ts`'s `megaHollow` already has here.
 *
 * At `ENCLOSURE_MID`, and at any enclosure while the strength is zero, the
 * three cuts are exactly 45, 30 and 35 — the module constants this replaces —
 * so the shipped world is byte-identical to the one before the dial existed.
 * The roster's mean enclosure is 0.505, so turning it is a spread about what
 * shipped rather than a shift of it.
 */
export const ENCLOSURE_MID = 0.5;

/** The authored range of `terrain.caveDensity` the enclosure is mapped onto. */
const CAVEDENSITY_LO = 0.38;
const CAVEDENSITY_HI = 0.56;

/**
 * `terrain.caveDensity` -> enclosure, **as authored** and before the strength.
 * Inverted: noise-made space is dissolved. See the block above for the roster.
 */
export function enclosureRawFor(caveDensity: number): number {
  const t = (CAVEDENSITY_HI - caveDensity) / (CAVEDENSITY_HI - CAVEDENSITY_LO);
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/**
 * The same, after `ENCLOSURE_STRENGTH`. **This is what worldgen reads**, and at
 * the shipped strength of zero it answers `ENCLOSURE_MID` for every biome.
 *
 * Split from the raw mapping above rather than folded into it so that the
 * authored ordering stays *testable*: at strength zero a test against this
 * function alone would pass for any mapping at all, including an inverted one,
 * which is exactly the vacuity this split was found by.
 */
export function enclosureFor(caveDensity: number): number {
  return ENCLOSURE_MID + (enclosureRawFor(caveDensity) - ENCLOSURE_MID) * ENCLOSURE_STRENGTH;
}

/** The authored range of `terrain.verticality` the spine thinning is mapped onto. */
const SPINE_VERT_LO = 0.2;
const SPINE_VERT_HI = 0.8;

/**
 * **How much of the redundant half of the forced sublattice a biome gives up**,
 * as authored and before the strength. Issue #57. `x` thins the shaft lines,
 * `y` thins the gallery lines; 0 is the shipped lattice and 1 is a mod-4 pitch
 * on that axis.
 *
 * A **V**, not a ramp, and the shape is the whole of the design: a biome in the
 * middle of the `verticality` roster is the shipped world, a flat one gives up
 * shafts, a tall one gives up galleries. A ramp would take *both* away from the
 * middle, which is the `(4,4)` corner of the sweep below — 78.7 % solid at one
 * probe against a 70 % ceiling, and the worst reading anything in this
 * experiment produced.
 *
 * `terrain.verticality` and not a thirteenth knob, and it is the *third* reader
 * for it (`caveAnisoFor`, `shelfPitchFor`, this) — the same rule `megaScaleFor`
 * and `enclosureFor` were added under.
 */
export function spineDropRawFor(verticality: number): { x: number; y: number } {
  const t = (verticality - SPINE_VERT_LO) / (SPINE_VERT_HI - SPINE_VERT_LO);
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  const x = 1 - 2 * c;
  const y = 2 * c - 1;
  // Snapped, not merely clamped: `(0.5 - 0.2) / 0.6` is 0.49999999999999994, so
  // the biome at the exact middle of the roster comes out 2.2e-16 away from the
  // shipped lattice rather than on it. A keep of 1 - 2.2e-16 is not >= 1 and
  // takes the hash path, which is a different world for no authored reason.
  const eps = 1e-9;
  return { x: x < eps ? 0 : x, y: y < eps ? 0 : y };
}

/**
 * The share of the redundant forced lines a biome **keeps**, after the
 * strength. 1 is the shipped lattice. This is what `worldgen.ts` reads.
 *
 * Split from `spineDropRawFor` for the reason `enclosureRawFor` is split from
 * `enclosureFor`, and it is a lesson this issue paid for once already: at
 * strength zero a test written against this function alone passes for any map
 * at all, including an inverted one.
 */
export function spineKeepXFor(verticality: number): number {
  return 1 - SPINE_STRENGTH * spineDropRawFor(verticality).x;
}
export function spineKeepYFor(verticality: number): number {
  return 1 - SPINE_STRENGTH * spineDropRawFor(verticality).y;
}

/**
 * **The amplitudes, and the strength that scales them — which ships at ZERO,
 * because the lever was measured and it is worth nothing.**
 *
 * The dial is real and wired: `enclosureFor` runs, `edgeHAt` reads it at every
 * edge, and at any non-zero strength the twelve biomes get twelve different
 * edge fields. It ships inert because turning it does not buy what #57 wants,
 * and *that* is the deliverable — the same shape `worldVoids`'s `megaHollow`
 * has, a provable no-op whose comment records what the sweep found so that the
 * next person does not spend the afternoon again.
 *
 * **WHAT IT COSTS**, sweeping all three together, 64 probes of 360x260 over
 * eight seeds and both modes (`tests/worldshape.test.ts`'s own geometry):
 *
 *     strength   solid fraction   worst standable   median run   longest run
 *       0.0      39.3 - 61.3           5.68 %           20           85
 *       1.0      38.6 - 61.4           5.69 %           20           85
 *       1.5      38.6 - 61.4           5.69 %           20           85
 *       2.0      38.6 - 61.4           5.69 %           20           85
 *       2.5      38.6 - 61.5           5.69 %           20           85
 *
 * Every movement contract holds at every setting, and the whole sweep is worth
 * **0.7 points of solid fraction at one probe of sixty-four** and nothing at all
 * anywhere else.
 *
 * **WHAT IT BUYS**, on `tests/worldSeparation.test.ts`'s Cohen's *f* — the
 * statistic #57's shape half is actually judged on:
 *
 *     strength   grainY   grainX   facePurity   edgeRatio   unitCount
 *       0.0       1.12     0.69       0.18        1.86        1.47
 *       1.5       1.13     0.70       0.10        1.87        1.44
 *       2.5       1.13     0.70       0.07        1.88        1.45
 *
 * **0.01 of D on the axis it was reached for, and `facePurity` moves the wrong
 * way.** No band's `grainY` moves by more than 0.15 against a within-band
 * spread of 2.4. At strength 1.5 it also takes `verdigris_spire` — the roster's
 * most built biome, and the one `docs/24` §7 flags as *"sitting on the band at
 * exactly 4.00"* — over `SIMULTANEOUS`, and its `shell` over 5 %. So it
 * perturbs two recorded sets to buy a hundredth of a separation statistic,
 * which is precisely the trade `docs/24` §7 refuses `MATERIAL_SPAN` 193 for.
 *
 * **WHY IT IS WEAK, AND THE REASON IS STRUCTURAL RATHER THAN A BAD SETTING.**
 * Three things bound it, and all three are load-bearing elsewhere:
 *
 *  1. **Half of every edge in the world is in the forced sublattice**, which
 *     `structure.ts` proves connected and 2-connected. Enclosure may only move
 *     the DOOR/OPEN split there, and the difference between a DOOR and an OPEN
 *     edge is a 2-cell partition — no open volume at all. That half is pinned
 *     by the connectivity guarantee, permanently.
 *  2. **Of the free half, only the WALL share moves volume**, and `MEGA_RIND_T`,
 *     the cave field and `ledges` have already decided most of it. The corridor
 *     router is two thirds of every open cell in the game (the note at the foot
 *     of `tests/worldVoids.test.ts`), but the *routed spine* is the forced
 *     sublattice, so what enclosure can withhold is only the redundant runs.
 *  3. **The visible half is a 3-cell wall on a 64-cell unit**, so the whole
 *     swing is +-0.5 points of a `palette.shell` that is already 3.1-6.7 %.
 *
 * The three shape inputs that *are* per-biome all took their range from levers
 * with mass — `caveAnisoFor` from the carve, `shelfPitchFor` from the deck
 * lattice, `megaScaleFor` from the mega layer. The edge field was the last
 * global one, and this is the measurement that says it has no mass to give.
 *
 * Turning it: set `ENCLOSURE_STRENGTH` and re-run `worldshape`,
 * `worldSeparation` and `paletteStructure`. `tests/worldArchitecture.test.ts`
 * asserts that the strength is zero and that the field nonetheless responds to
 * an enclosure handed to it directly, so neither half of "wired but inert" is
 * left as a claim in a comment.
 */
const WALL_MID = 45;
const WALL_AMP = 6;
const DOOR_MID = 30;
const DOOR_AMP = 4;
const FORCED_DOOR_MID = 35;
const FORCED_DOOR_AMP = 8;

/**
 * Multiplier on all three amplitudes. **Zero, and the zero is measured** — see
 * the block above. At zero the three cuts are exactly 45, 30 and 35, which are
 * the module constants this replaced, so the shipped world is byte-identical to
 * the one before the dial existed.
 */
export const ENCLOSURE_STRENGTH = 0;

/**
 * **Multiplier on the per-biome thinning of the forced sublattice — ZERO, and
 * the zero is where the movement contract starts failing rather than a default
 * nobody turned.** Issue #57.
 *
 * The dial is real and wired: at any non-zero strength the twelve biomes get
 * twelve different sublattices, a flat one giving up shaft lines and a tall one
 * giving up gallery lines, with the mod-4 connectivity guarantee intact at
 * every setting (see `rowForced`). What it buys and what it costs, on
 * `tests/worldSeparation.test.ts`'s Cohen's *f* and `tests/worldshape.test.ts`'s
 * own 64 probes:
 *
 *     strength  D grainY  D grainX   solid %      standable  medRun  longRun
 *       0.0  *    1.12      0.69     39.3-61.3      5.68 %     20      85
 *       0.25      1.18      0.60     39.5-61.3      5.30 %     19 x    74
 *       0.5       1.16      0.64     40.9-62.3      5.03 %     17 x    74
 *       1.0       1.36      1.05     41.8-65.9      4.82 % x   17 x    64
 *       bars                          38 - 70       >= 5 %    >= 20   >= 60
 *
 * At full strength it is the **largest movement any layout change has made
 * since the mega layer**, and the first time `grainX` has reached the material
 * bar of 1.04 that `worldSeparation` sets its thresholds from. It is also two
 * broken contracts, and the smallest setting tried is already over the line:
 * the median walkable run ships at exactly its floor of 20 and 0.25 takes it to
 * 19, for 0.06 of `grainY` and *minus* 0.09 of `grainX`.
 *
 * The full eight-setting hard-pitch sweep that led here, and why the mega layer
 * and the room vocabulary were dismissed on numbers that already existed, are
 * at the head of `tests/worldSpine.test.ts`.
 */
export const SPINE_STRENGTH = 0;

/**
 * The three percentile cuts at an enclosure: WALL below `wall`, DOOR between
 * `wall` and `wall + door`, OPEN above; and `forcedDoor` for the sublattice
 * `structure.ts` proves connected, where the choice is only DOOR or OPEN.
 *
 * Exported so the sweep in the block above is assertable rather than quoted.
 */
export function edgeCuts(enc: number): { wall: number; door: number; forcedDoor: number } {
  const d = (enc - ENCLOSURE_MID) * 2;
  return {
    wall: WALL_MID + d * WALL_AMP,
    door: DOOR_MID + d * DOOR_AMP,
    forcedDoor: FORCED_DOOR_MID + d * FORCED_DOOR_AMP,
  };
}

/** Percentile below which a non-forced edge is a WALL. */
function wallCut(enc: number): number {
  return WALL_MID + (enc - ENCLOSURE_MID) * 2 * WALL_AMP;
}
/** How wide the DOOR band is above the WALL cut; the rest is OPEN. */
function doorWidth(enc: number): number {
  return DOOR_MID + (enc - ENCLOSURE_MID) * 2 * DOOR_AMP;
}
/** Percentile below which a *forced-passable* edge is a DOOR rather than OPEN. */
function forcedDoorCut(enc: number): number {
  return FORCED_DOOR_MID + (enc - ENCLOSURE_MID) * 2 * FORCED_DOOR_AMP;
}

/**
 * Connection type of the edge between unit (rx,ry) and (rx+1,ry).
 *
 * Forced-passable rule: every horizontal edge on an even unit row and every
 * vertical edge on an even unit column is guaranteed passable — see
 * `rowForced`/`colForced`, which are that rule and the only copy of it. That
 * sublattice is a 2-unit-pitch grid graph — connected AND full of cycles, which
 * is what buys the redundancy guarantee (structure.ts verifyRedundancy):
 * between any two adjacent path nodes there is always a second route around a
 * face. `tests/worldSpine.test.ts` asserts it instead of leaving it as this
 * sentence.
 *
 * The forced edges are *not* a visible grid: they are corridors whose crossing
 * points are jittered per edge, they are punched through herringbone rooms that
 * never line up, and ~35% of them are DOOR rather than OPEN so long sightlines
 * do not form. The tiles are what the player reads; these are what the router
 * reads.
 */
export function edgeH(
  rx: number,
  ry: number,
  seed: number,
  enc = ENCLOSURE_MID,
  keep = 1,
): EdgeTypeId {
  // Interior to a horizontal tile — one room, always open.
  if (mod4(rx + ry) === 0) return EdgeType.OPEN;
  const h = hash2(rx, ry, seed ^ 0x1357abcd);
  if (rowForced(rx, ry, seed, keep)) {
    return h % 100 < forcedDoorCut(enc) ? EdgeType.DOOR : EdgeType.OPEN;
  }
  const r = h % 100;
  const w = wallCut(enc);
  return r < w ? EdgeType.WALL : r < w + doorWidth(enc) ? EdgeType.DOOR : EdgeType.OPEN;
}

/** Connection type of the edge between unit (rx,ry) and (rx,ry+1). */
export function edgeV(
  rx: number,
  ry: number,
  seed: number,
  enc = ENCLOSURE_MID,
  keep = 1,
): EdgeTypeId {
  // Interior to a vertical tile.
  if (mod4(rx + ry) === 2) return EdgeType.OPEN;
  const h = hash2(rx, ry, seed ^ 0x2468bdf1);
  if (colForced(rx, ry, seed, keep)) {
    return h % 100 < forcedDoorCut(enc) ? EdgeType.DOOR : EdgeType.OPEN;
  }
  const r = h % 100;
  const w = wallCut(enc);
  return r < w ? EdgeType.WALL : r < w + doorWidth(enc) ? EdgeType.DOOR : EdgeType.OPEN;
}

/**
 * **THE FORCED SUBLATTICE, THINNED PER BIOME — AND THE ONE PROPERTY THAT MAY
 * NOT MOVE WHILE IT IS.** Issue #57.
 *
 * Half of every edge in this world is forced passable, and until now the rule
 * was two characters wide in two files: `(ry & 1) === 0` in `edgeH` and again,
 * copied, in `worldgen.ts` `routeCorridors`. These two functions are that rule,
 * declared once, and everything below is why the pitch is what it is.
 *
 * **The invariant, at every setting including the ones that are not shipped.**
 * `structure.ts` `verifyRedundancy` needs the forced set to be connected *and*
 * 2-connected. Both functions answer `true` unconditionally on the **mod-4**
 * lattice — horizontal lines at `ry ≡ 0 (mod 4)`, vertical at `rx ≡ 0 (mod 4)`,
 * every edge along them, in every biome, at every `keep`. That is a complete
 * grid graph of 4x4-unit faces, so it is connected and every edge lies on a
 * cycle no matter what `keep` does to the rest. The thinning is only ever
 * applied to the mod-2-but-not-mod-4 lines, which are redundancy the guarantee
 * does not rest on. `tests/worldSpine.test.ts` asserts this directly rather
 * than leaving it as the argument it used to be.
 *
 * `keep` is a probability rather than a pitch for that reason: a coarser pitch
 * would move the guaranteed grid itself, and a per-biome guaranteed grid is a
 * per-biome connectivity guarantee.
 */
export function rowForced(rx: number, ry: number, seed: number, keep = 1): boolean {
  if ((ry & 1) !== 0) return false;
  if ((ry & 3) === 0) return true;
  return keep >= 1 || hash2(rx, ry, seed ^ 0x6b1de5a3) % 1024 < keep * 1024;
}

/** The same for the vertical edge between (rx,ry) and (rx,ry+1). */
export function colForced(rx: number, ry: number, seed: number, keep = 1): boolean {
  if ((rx & 1) !== 0) return false;
  if ((rx & 3) === 0) return true;
  return keep >= 1 || hash2(rx, ry, seed ^ 0x2c07f1b9) % 1024 < keep * 1024;
}

/** True when a unit sits on the routed critical path (grid intersections). */
export function isPathUnit(rx: number, ry: number): boolean {
  return (rx & 1) === 0 && (ry & 1) === 0;
}

// ------------------------------------------------------------- node geometry

/** Routing node of a unit, in world cells. Jittered so corridors wander. */
export function nodeX(rx: number, ry: number, seed: number): number {
  return (rx << RU_SHIFT) + 18 + (hash2(rx, ry, seed ^ 0x5a17c0de) % (RU - 36));
}
export function nodeY(rx: number, ry: number, seed: number): number {
  return (ry << RU_SHIFT) + 18 + (hash2(rx, ry, seed ^ 0x7b91f00d) % (RU - 36));
}

/** Where the (rx,ry)-(rx+1,ry) corridor crosses the shared border. */
export function crossHY(rx: number, ry: number, seed: number): number {
  return (ry << RU_SHIFT) + 14 + (hash2(rx, ry, seed ^ 0x3c5d7e9f) % (RU - 28));
}
/** Where the (rx,ry)-(rx,ry+1) corridor crosses the shared border. */
export function crossVX(rx: number, ry: number, seed: number): number {
  return (rx << RU_SHIFT) + 14 + (hash2(rx, ry, seed ^ 0x4d6e8fa0) % (RU - 28));
}

// ------------------------------------------------------------------ variants

export const ROOM_ARCHETYPES = ['arena', 'corridor', 'shaft', 'vault', 'cache'] as const;
export type RoomArchetype = (typeof ROOM_ARCHETYPES)[number];

/**
 * Four shapes x three dressings = 12 variants per archetype. A biome declares
 * three archetypes (biomes.ts `rooms`), so every biome ships 36 distinct room
 * bodies before palette and cave noise are applied — docs/05 §2 asks for
 * "3 room templates x 12 variants".
 */
export const ROOM_SHAPES: Record<RoomArchetype, readonly string[]> = {
  arena: ['bowl', 'pillared', 'tiered', 'pit'],
  corridor: ['straight', 'colonnade', 'zigzag', 'ribbed'],
  shaft: ['plain', 'staggered', 'spiral', 'chimney'],
  vault: ['strongroom', 'reliquary', 'oubliette', 'magazine'],
  cache: ['niche', 'pocket', 'cellar', 'shrine'],
};

export const ROOM_DRESSINGS = ['bare', 'rubble', 'overgrown'] as const;
export type RoomDressing = (typeof ROOM_DRESSINGS)[number];

/** How many passable exterior edges an archetype can host, and its grain. */
interface ArchetypeRule {
  minOpen: number;
  maxOpen: number;
  /** Orientation the archetype wants; 'any' fits either tile. */
  orient: 'h' | 'v' | 'any';
  /** Sealed by ADAMANT_SEAL rather than a destructible shell. */
  sealed: boolean;
}

const ARCHETYPE_RULES: Record<RoomArchetype, ArchetypeRule> = {
  // A vault only exists where the edge field dealt it at most one connection.
  // Its walls are ADAMANT_SEAL, so the single door is genuinely the only way
  // in — you cannot dig around a strongroom. (A tile can never be dealt *zero*
  // passable edges: the forced sublattice guarantees at least one, since a
  // tile always spans both parities on one of its axes. Fully sealed rooms are
  // therefore placed separately, as pockets inside bedrock — see
  // WorldGen.sealedVaults.)
  vault: { minOpen: 0, maxOpen: 1, orient: 'any', sealed: true },
  cache: { minOpen: 1, maxOpen: 2, orient: 'any', sealed: false },
  corridor: { minOpen: 2, maxOpen: 6, orient: 'h', sealed: false },
  shaft: { minOpen: 2, maxOpen: 6, orient: 'v', sealed: false },
  arena: { minOpen: 1, maxOpen: 6, orient: 'any', sealed: false },
};

export interface RoomVariant {
  archetype: RoomArchetype;
  shape: string;
  dressing: RoomDressing;
  sealed: boolean;
}

export interface VariantCandidate {
  archetype: RoomArchetype;
  weight: number;
}

/**
 * The legal archetypes for a tile slot's signature, and their weights —
 * the candidate set `chooseVariant` rolls against, extracted so issue #184's
 * WFC spike (`src/world/wfcSpike.ts`) can ask the same question `chooseVariant`
 * asks (what may legally go here, and how does the biome lean) without
 * re-deriving `ARCHETYPE_RULES`' minOpen/maxOpen/orient logic a second place
 * to drift from. Pure extraction — `chooseVariant` below calls this and does
 * exactly what it did before.
 */
export function variantCandidates(
  biome: BiomeDef,
  orient: TileOrientId,
  openCount: number,
): readonly VariantCandidate[] {
  const wantOrient = orient === TileOrient.H ? 'h' : 'v';

  // Candidate set: the biome's three identity archetypes, plus vault/cache as
  // universal secondary types (every biome has hidden rooms and stashes).
  const out: VariantCandidate[] = [];
  for (const a of ROOM_ARCHETYPES) {
    const rule = ARCHETYPE_RULES[a];
    if (openCount < rule.minOpen || openCount > rule.maxOpen) continue;
    const primary = biome.rooms.indexOf(a) >= 0;
    if (!primary && a !== 'vault' && a !== 'cache') continue;
    let w = primary ? 4 : 1;
    if (rule.orient === wantOrient) w *= 2.5;
    else if (rule.orient !== 'any') w *= 0.35;
    // Verticality pushes shafts, flatness pushes corridors and arenas.
    if (a === 'shaft') w *= 0.5 + biome.terrain.verticality * 1.6;
    if (a === 'corridor' || a === 'arena') w *= 1.4 - biome.terrain.verticality * 0.7;
    out.push({ archetype: a, weight: w });
  }
  return out;
}

/** `ARCHETYPE_RULES[a].sealed`, for callers outside this file that only have an archetype. */
export function archetypeSealed(a: RoomArchetype): boolean {
  return ARCHETYPE_RULES[a].sealed;
}

/**
 * Pick a variant for a tile slot: biome's preferred archetypes first, filtered
 * to those whose edge rule accepts this slot's signature, orientation-weighted.
 * Falls back through cache -> arena so a slot is never left unresolved.
 *
 * `forcedArchetype` is issue #184's WFC-spike seam: when given, and legal for
 * this slot's own candidate set, it is used in place of the weighted roll —
 * everything else (shape, dressing, `sealed`) still resolves exactly as it
 * does on the hash path. An illegal or `undefined` forced archetype falls
 * through to the roll unchanged, so a caller that never passes it — every
 * caller today except the spike's own measurement harness — sees no
 * behavioural difference at all.
 */
export function chooseVariant(
  biome: BiomeDef,
  orient: TileOrientId,
  openCount: number,
  rng: Rng,
  out: RoomVariant,
  forcedArchetype?: RoomArchetype,
): RoomVariant {
  const cands = variantCandidates(biome, orient, openCount);

  let arch: RoomArchetype;
  if (forcedArchetype !== undefined && cands.some((c) => c.archetype === forcedArchetype)) {
    arch = forcedArchetype;
  } else if (cands.length === 0) {
    // Signature nobody claimed. openCount 0 must be sealed; anything else is
    // an arena, which accepts 1..6.
    arch = openCount === 0 ? 'vault' : 'arena';
  } else {
    arch = rng.pickWeighted(
      cands.map((c) => c.archetype),
      cands.map((c) => c.weight),
    );
  }

  const shapes = ROOM_SHAPES[arch];
  out.archetype = arch;
  out.shape = shapes[rng.u32() % shapes.length];
  out.dressing = ROOM_DRESSINGS[rng.u32() % ROOM_DRESSINGS.length];
  out.sealed = ARCHETYPE_RULES[arch].sealed;
  return out;
}

export function makeRoomVariant(): RoomVariant {
  return { archetype: 'arena', shape: 'bowl', dressing: 'bare', sealed: false };
}

// --------------------------------------------------------------- tile build

/**
 * Protection level at or above which the tiler leaves a cell alone.
 *
 * `docs/20` §9.2: a mega feature "must write a suppression mask that
 * `placeTiles`, `routeCorridors` and `ledges` all read". This is the tiler's
 * half of it. Without it a 50x27 room body and its 3-cell shell get stamped
 * straight through a 400-cell cavern — a tile is 1/96th of a screen and the
 * cavern is half of one, so the small thing would be erasing the large thing,
 * which is the wrong way round for every reason in `docs/21` §2.5(d).
 *
 * The number is `worldgen.ts`'s `P_MEGA`. It is declared here rather than
 * imported from there because `worldgen` imports this module and not the other
 * way about, and `tests/worldVoids.test.ts` asserts the two are equal — a
 * duplicated constant that nothing checks is a mask that silently stops masking.
 */
export const PROTECT_MEGA = 3;

/**
 * **The level an authored tile writes, and the reason it is not a new number.**
 *
 * `docs/20` §9.2 is quoted three lines up: a large authored feature *"must write
 * a suppression mask that `placeTiles`, `routeCorridors` and `ledges` all
 * read"*. An authored room is exactly such a feature, and without the mask it
 * does not survive its own chunk: `routeCorridors` runs **after** `placeTiles`,
 * owns two thirds of every open cell in the game (`tests/routerBudget.test.ts`
 * measures the decomposition — tiles take the world to 26.3 % open, the router
 * to 58.1 %), and reads nothing the tiler wrote. Measured on the shipped seed
 * before this existed: **the router and `ledges` between them rewrote 25–57 %
 * of a placed cistern**, cutting a 23-cell shaft through the vault and laying
 * deck slabs across the tank. The drawing was placed and then deleted.
 *
 * This is `worldgen.ts`'s `P_MASS`, not a fifth level, and that is a decision
 * rather than laziness. Every downstream reader already gives `P_MASS` exactly
 * the answers an authored room wants, and each of them for a reason written at
 * `P_MASS`'s own declaration:
 *
 *  - `carveH`/`carveV`: *"a mega mass is rock the router goes around — unless
 *    this is the critical path, in which case it goes through"*. So the room is
 *    not shredded, **and connectivity is untouched**: the routed spine still
 *    tunnels in, which is the doorway an authored room wants anyway.
 *  - `ledges`: skips every non-zero level that is not `P_MEGA`, so no deck is
 *    laid through the tank.
 *  - `blobFill` (`pools`, `hazardPockets`): explicitly *allows* `P_MASS`, so a
 *    cistern can still fill with water. That is the one pass we want to keep.
 *
 * A fifth level would mean auditing ten readers to make them answer what this
 * one already answers, and `docs/15` §3.2's rule on second spellings applies to
 * enum values as much as to file formats.
 *
 * `tests/tileMask.test.ts` asserts this equals `worldgen.ts`'s `P_MASS`, for the
 * reason `tests/worldVoids.test.ts` asserts it for `PROTECT_MEGA`: a duplicated
 * constant that nothing checks is a mask that silently stops masking.
 */
export const PROTECT_AUTHORED = 4;

export interface TileCtx {
  /** Per-cell kind buffer for the chunk being generated. */
  kind: Uint8Array;
  /**
   * Per-cell protection buffer, same indexing as `kind`. Cells at
   * `PROTECT_MEGA` or above are not the tiler's to write.
   */
  protect: Uint8Array;
  /** Chunk origin in world cells. All writes are clipped to the chunk. */
  baseX: number;
  baseY: number;
  /** Tile rect in world cells. */
  x: number;
  y: number;
  w: number;
  h: number;
  orient: TileOrientId;
  /** Six exterior edge types. H: [W,E,N0,N1,S0,S1]. V: [N,S,W0,W1,E0,E1]. */
  edges: Int32Array;
  openCount: number;
  biome: BiomeDef;
  variant: RoomVariant;
  /** Stable per-tile hash — dressing uses it so detail never depends on order. */
  seed: number;
  rng: Rng;
}

export function makeTileCtx(kind: Uint8Array, protect: Uint8Array): TileCtx {
  return {
    kind,
    protect,
    baseX: 0, baseY: 0,
    x: 0, y: 0, w: 0, h: 0,
    orient: TileOrient.H,
    edges: new Int32Array(6),
    openCount: 0,
    biome: null as unknown as BiomeDef,
    variant: makeRoomVariant(),
    seed: 0,
    rng: new Rng(1),
  };
}

/** Clipped rect fill in world coords. Row base is precomputed — no per-cell branch. */
function rect(ctx: TileCtx, x: number, y: number, w: number, h: number, v: number): void {
  const x0 = x < ctx.baseX ? ctx.baseX : x;
  const y0 = y < ctx.baseY ? ctx.baseY : y;
  const xe = ctx.baseX + CHUNK_SIZE;
  const ye = ctx.baseY + CHUNK_SIZE;
  const x1 = x + w > xe ? xe : x + w;
  const y1 = y + h > ye ? ye : y + h;
  if (x1 <= x0 || y1 <= y0) return;
  const kind = ctx.kind;
  const prot = ctx.protect;
  for (let wy = y0; wy < y1; wy++) {
    const row = ((wy - ctx.baseY) << CHUNK_SHIFT) - ctx.baseX;
    for (let wx = x0; wx < x1; wx++) {
      const i = row + wx;
      if (prot[i] >= PROTECT_MEGA) continue;
      kind[i] = v;
    }
  }
}

/** Clipped filled ellipse in world coords. */
function ellipse(ctx: TileCtx, cx: number, cy: number, rx: number, ry: number, v: number): void {
  if (rx < 1 || ry < 1) return;
  const y0 = Math.max(ctx.baseY, cy - ry);
  const y1 = Math.min(ctx.baseY + CHUNK_SIZE - 1, cy + ry);
  const kind = ctx.kind;
  const prot = ctx.protect;
  const inv = 1 / (ry * ry);
  for (let wy = y0; wy <= y1; wy++) {
    const dy = wy - cy;
    const t = 1 - dy * dy * inv;
    if (t <= 0) continue;
    const span = (rx * Math.sqrt(t)) | 0;
    const x0 = Math.max(ctx.baseX, cx - span);
    const x1 = Math.min(ctx.baseX + CHUNK_SIZE - 1, cx + span);
    const row = ((wy - ctx.baseY) << CHUNK_SHIFT) - ctx.baseX;
    for (let wx = x0; wx <= x1; wx++) {
      const i = row + wx;
      if (prot[i] >= PROTECT_MEGA) continue;
      kind[i] = v;
    }
  }
}

// ------------------------------------------------------------ authored tiles

/**
 * **The class vocabulary an authored tile is drawn in, and the whole of the
 * mapping.**
 *
 * `docs/19` §8.3 asks for *"a `Kind` that resolves to the local palette at
 * `materialise` time, so an authored stamp's rock is the surrounding biome's
 * rock and its border is invisible"*, and calls it *"the single cheapest item
 * here and we do not have it"*. The honest reading of this table is that we
 * already had all four — `Kind.SOLID` **is** fill-from-biome, and issue #143
 * made it mean that at region scale instead of per cell. What was missing was
 * not the class; it was a way to *draw* with it.
 *
 * **It is seven now, and the three that arrived are the three remaining
 * `BiomePalette` slots a static cell can name.** `DEFERRED_CLASSES` carries the
 * derivation; the short version is that `primary`/`secondary` are `@host`
 * between them, the three fluid slots cannot be drawn because a drawn cell is
 * `Flag.STATIC` and a pool that does not flow is not a pool, and what is left is
 * `shell`, `platform`, `fill`, `accents`, `glow`. This table is the *only*
 * statement of the mapping; `tools/` reads `DEFERRED_CLASSES` and
 * `kindForClass` rather than repeating either.
 *
 * `hasOwnProperty` rather than `TABLE[cls] ?? TABLE.host`, which is the house
 * rule (`weaponVisual.ts`, `pixelBody.ts`, `planText.ts`'s `own`) and is not
 * decoration: a bare index answers `Function.prototype.toString` for
 * `cls === 'toString'`, and every caller downstream then writes a function into
 * a `Uint8Array` as `0`. That exact defect has been found live four times in
 * this repo.
 */
const KIND_FOR_CLASS: Readonly<Record<DeferredClass, KindId>> = {
  host: Kind.SOLID,
  shell: Kind.SHELL,
  platform: Kind.PLATFORM,
  gate: Kind.GATE,
  vein: Kind.VEIN,
  glow: Kind.GLOW,
  fill: Kind.FILL,
};

export function kindForClass(cls: string): KindId | undefined {
  return Object.prototype.hasOwnProperty.call(KIND_FOR_CLASS, cls)
    ? KIND_FOR_CLASS[cls as DeferredClass]
    : undefined;
}

/** One authored room unit: the document, and the cell classes it resolves to. */
export interface AuthoredTile {
  readonly name: string;
  /**
   * Where the drawing lives, repo-relative.
   *
   * Carried so that a tool listing the authored assets — `pixel tile edit`'s
   * picker — gets the path from the registry that loads them rather than by
   * scanning a directory itself. A tool that walked `src/world/tiles/` could
   * offer a file the build has not got, which is the seam `tile show` already
   * refuses one half of.
   */
  readonly path: string;
  readonly doc: ShapeMaskDoc;
  readonly orient: TileOrientId;
  /** `doc.w * doc.h` cell classes, in `Kind` space. */
  readonly kinds: Uint8Array;
  /**
   * `doc.w * doc.h`, `-1` for a cell with no pool, else the index into
   * `pools` — issue #183. Resolved once per tile at load time, same as
   * `kinds`; what varies per placement is only *which* entry of `pools[k]`
   * a cell reads as, decided in `stampAuthored`.
   */
  readonly poolIndex: Int16Array;
  /** `doc.pools`, each resolved through `kindForClass` once. Parallel to `doc.pools`. */
  readonly pools: readonly ResolvedPool[];
}

/**
 * One legend pool, resolved from classes to `Kind` ids — the load-time half
 * of a pool. `kinds`/`weights` are parallel arrays in file order, so index
 * `k` of one is the entry index `k` of the other; the per-placement roll
 * (`stampAuthored`) picks an index into both by weight.
 */
export interface ResolvedPool {
  readonly kinds: readonly KindId[];
  readonly weights: readonly number[];
  readonly totalWeight: number;
}

/**
 * Resolve a document's legend pools into `Kind` space, and map every pool
 * cell to which pool it belongs to.
 *
 * Split out of `tilesFrom` because it is the seed-free half of a pool —
 * `stampAuthored` is the other half, and it takes what this function built
 * rather than re-deriving it per placement.
 */
function resolvePools(doc: ShapeMaskDoc): { pools: readonly ResolvedPool[]; poolIndex: Int16Array } {
  const pools: ResolvedPool[] = doc.pools.map((p: LegendPool) => {
    const kinds = p.entries.map((e) => kindForClass(e.cls) as KindId);
    const weights = p.entries.map((e) => e.weight);
    let totalWeight = 0;
    for (const w of weights) totalWeight += w;
    return { kinds, weights, totalWeight };
  });
  const poolAt = new Map<string, number>();
  doc.pools.forEach((p, idx) => poolAt.set(p.ch, idx));
  const poolIndex = new Int16Array(doc.w * doc.h).fill(-1);
  for (let y = 0; y < doc.h; y++) {
    const row = doc.rows[y];
    for (let x = 0; x < doc.w; x++) {
      const idx = poolAt.get(row[x]);
      if (idx !== undefined) poolIndex[y * doc.w + x] = idx;
    }
  }
  return { pools, poolIndex };
}

/**
 * **How often a tile slot is an authored room: one in this many, and the number
 * is set by what the palette can pay rather than by what `docs/19` asks for.**
 *
 * `docs/19` §8.3's target for authored area is **2–7 % of generated area**
 * against our 0.36 % today, and it is emphatic that *"the gap is almost entirely
 * piece size, not piece count"* — our set pieces are 0.03–0.13 screens against
 * Noita's 0.26–1.16. A tile-sized authored room closes the size half by
 * construction. **The rate half did not close, and the reason is a measurement
 * rather than caution.**
 *
 * **The ceiling used to be one palette slot, and it is not any more.** This
 * docblock previously recorded the sweep that set this constant to 64: an
 * authored solid had exactly one class, `Kind.SHELL`, so every drawn room spent
 * `palette.shell`, which `tests/paletteStructure.test.ts` gates at 5 % of solid
 * area with `chlorine_fen` at **4.951 %** and `nanite_reef` at **4.837 %**. At
 * one room in 32 slots both biomes crossed the gate outright. The rate was set
 * by a palette slot rather than by the drawing.
 *
 * **Two things moved since, and they multiply.** `DEFERRED_CLASSES` went from
 * four words to seven, so `cistern.pxm` spends 344 of its 1264 authored cells on
 * `@shell` instead of 1090 of 1090 — **32 % of what a room used to cost the
 * saturated slot**. And `stope.pxm` gave the pool a *vertical* room, so the half
 * of a herringbone lattice that is vertical stopped answering `null`; the
 * realised rate is now the declared one rather than half of it.
 *
 * **The sweep, re-run with both of those in the tree** (`palette.shell` as a
 * percentage of solid area, the two saturated biomes, and what
 * `tests/paletteStructure.test.ts` says as a whole):
 *
 *     AUTHORED_PERIOD   chlorine_fen   nanite_reef   authored slots   paletteStructure
 *       (none)             4.951          4.837        0.00 %         baseline
 *       32                 5.031          4.946        2.95 %         SHELL_OVER_FIVE regressed
 *     * 48                 4.986          4.867        1.97 %         counterfactual moved
 *       64                 4.980          4.910        1.51 %         counterfactual moved
 *
 * At 32 `chlorine_fen` crosses the gate again — the vocabulary bought a factor
 * of three on the *cost per room* and the vertical tile spent a factor of two of
 * it on *coverage*, so the slot is back in play one step further down. 48 was
 * the largest rate that cleared it, and it cleared it by 0.014 points rather
 * than by accident.
 *
 * **Re-swept when the pool went from two tiles to six** (`chlorine_fen` shell,
 * `meatworks` folded against `SIMULTANEOUS` = 4, slots authored):
 *
 *     AUTHORED_PERIOD   chlorine_fen   meatworks folded   authored slots
 *       32                 5.021            3.97            2.95 %
 *     * 36                 4.979            4.03            2.63 %
 *       40                 5.020            3.97
 *       44                 5.003            4.03
 *       48                 4.986            4.03            1.97 %
 *
 * Two things the table says, and the second matters more than the first. The
 * gate margin is **not monotone in the rate** — 44 fails where 48 and 36 clear
 * — because every rate re-deals `hash % AUTHORED_PERIOD` and the gate rides
 * within ±0.03 points of 5 % at all of them; "the largest rate that clears" is
 * therefore no longer a stable derivation, and the rule it leaves behind is
 * *the widest margin at the most coverage*. That is 36: it clears the shell
 * gate by 0.021 — wider than 48's own 0.014 — at 2.63 % of slots against
 * `docs/19` §8.3's 2–7 % band, inside the band for the first time. And
 * `meatworks` folded is 4.03 at 36 exactly as it was at 48 — the recorded
 * exceedance (`FOLDED_OVER_SIMULTANEOUS`, with its glowVeins measurement)
 * carries over unchanged, spent once rather than twice.
 *
 * **Re-swept at sixteen tiles, and 36 still stands.** Every step down breaks
 * a real band rather than a snapshot: 32 puts `chlorine_fen`'s shell back
 * over the gate at 5.029 %, 28 pushes a biome over `SIMULTANEOUS` on the
 * *shipped* count — the band itself, not the counterfactual — and 24 does
 * both. Pool size buys variety, not rate: the palette gates are saturated by
 * how much authored area exists, not by how many drawings supply it, so the
 * next coverage lever is not here. (What sixteen tiles *did* buy the metrics:
 * the `meatworks` folded exceedance healed at this size — its GLOWCAP window
 * re-dealt back under the 5 % floor — and the entry retired.)
 *
 * **`PLATFORM_COUNTERFACTUAL` moves at every rate in the table and is not the
 * binding constraint.** It is a two-decimal snapshot of a *count* of materials
 * over 5 % averaged over eighteen windows, so one window flipping moves it by
 * 0.056; it is re-recorded there with the five rows that moved and the reason.
 * What it is *worth reading for* is `emberworks`, which moved 4.61 → 4.89 —
 * five windows, not noise — and whose folded column now sits at **3.89 against
 * `SIMULTANEOUS` = 4**. That is the second, tighter reason 48 is the ceiling:
 * the next step down would be spending a band rather than a snapshot.
 *
 * **So the honest figure: 1 slot in 36, both orientations — 2.63 % of slots
 * authored**, against **1.97 %** at 48 and **0.78 %** before the vertical tile.
 * `docs/19` §8.3's target is 2–7 % of generated area, so this is inside the
 * band rather than at its floor. Two honest caveats, both measured in
 * `tests/tileMask.test.ts`: a placed room keeps a mean **65 %** of its own
 * mask once mega masses and the router have been over it, so realised authored
 * *area* is lower; and the band is stated over area rather than over slots,
 * which is the same conversion §8.3 itself makes.
 *
 * **The pool constraint that held this at 48 has eased but not gone.**
 * Barrett's floor is *"minimum 16 pieces at two edge variations"* (§7, the one
 * primary-sourced number in the document) and Noita ships 144 for the Mines;
 * we ship **six** — three per orientation, so a slot's neighbour repeating it
 * is a 1-in-3 rather than a certainty. Rates below 32 are still priced by the
 * shell gate, and rates at 32 by it outright (5.021 in the table above); the
 * next real lever is more drawings, which is the cheapest item on this list
 * and needs no source edit.
 */
export const AUTHORED_PERIOD = 36;

/**
 * **A tile slot's own hash — the one statement of it.**
 *
 * `worldgen.ts` computed this inline as `hash2(ux, uy, seed ^ (orient ? 0x7711
 * : 0x3355))`, which was fine while nothing else needed to know a tile's
 * identity. `authoredTileAt` needs it, and so does anything that wants to ask
 * *where the authored rooms are* without generating a world — `pixel tile show`
 * has to find a placed instance to render, and a tool that recomputed the salts
 * itself would be `tests/weaponSweep.test.ts` again: a viewer that transcribes
 * drifts, and is worse than none because it is believed.
 *
 * So it is declared here and called from both places, which is the shape
 * `rowForcedAt` already has — *"there is no assertion here because there is no
 * longer anything to assert: one implementation, called from both places."*
 */
export function tileSeedFor(ux: number, uy: number, orient: TileOrientId, seed: number): number {
  return hash2(ux, uy, seed ^ (orient === TileOrient.V ? 0x7711 : 0x3355));
}

/**
 * Which slot a `w × h` drawing is, or a throw naming both sizes it could be.
 *
 * Orientation is **read off the aspect rather than declared**, so a tile cannot
 * claim to be horizontal and be drawn vertical, and there is no field for the
 * two to disagree in. A slot is `2·RU × RU` or `RU × 2·RU` and nothing else, so
 * anything that is neither is a size error caught at load — with the file named
 * — rather than a stamp that runs off the end of its own tile.
 *
 * Its own function rather than four lines inside `authoredTiles`, and that is a
 * mutation-testing result rather than tidiness: inlined, the `else` branch is
 * unreachable while every shipped tile is the right size, so deleting the check
 * altogether broke nothing any test could see.
 */
export function orientOfRoomUnit(w: number, h: number, where = '<tile>'): TileOrientId {
  if (w === RU * 2 && h === RU) return TileOrient.H;
  if (w === RU && h === RU * 2) return TileOrient.V;
  throw new Error(
    `${where}: ${w}x${h} is not a room unit — a tile is ${RU * 2}x${RU} or ${RU}x${RU * 2} at RU ${RU}`,
  );
}

let cachedAuthored: readonly AuthoredTile[] | null = null;

/**
 * **Parse a set of authored tiles into a pool whose order is a pure function of
 * their names.**
 *
 * This is the determinism obligation the glob brings with it, and it is not
 * hypothetical. `authoredTileAt` picks with
 * `pool[hash2(uy, ux, …) % pool.length]`, so **which room a slot gets is a
 * function of the pool's order** — and a directory listing is not an ordering
 * anybody promised. A hand-written array was an ordering, badly: it was one
 * somebody had to remember to keep.
 *
 * So the entries are sorted by path here, once, and nothing downstream is
 * allowed to care where they came from. Same files ⇒ same order ⇒ same room in
 * the same slot at the same seed, whatever order Vite, a filesystem or a future
 * bundler hands them over in. `tests/tileMask.test.ts` proves it by handing this
 * function a shuffled set and requiring the same pool back.
 *
 * Note what is *not* claimed: adding a room changes which slots get which room,
 * because `% pool.length` moves. That is inherent in having a pool at all — it
 * is the same statement as "a new room appears in the world" — and it is
 * seeded, not random.
 *
 * Exported for the test above and for no runtime caller; `authoredTiles` is the
 * one the game uses.
 */
export function tilesFrom(sources: Record<string, string>): readonly AuthoredTile[] {
  // A `Map`, never an object index, so a file called `toString.pxm` cannot
  // resolve to `Function.prototype.toString` — the trap `docs/25` §5.2 records
  // four live instances of. `kindForClass` above spells it with
  // `hasOwnProperty`; there is no key to index here at all.
  const byPath = new Map<string, string>();
  for (const key of Object.keys(sources)) {
    // Glob keys are relative to this module (`./tiles/cistern.pxm`). Repo-relative
    // is what a person and a tool both want to see.
    byPath.set(key.replace(/^\.\//, 'src/world/'), sources[key]);
  }
  return [...byPath.keys()].sort().map((path) => {
    const text = byPath.get(path) as string;
    const doc = parseShapeMask(text, path);
    const orient = orientOfRoomUnit(doc.w, doc.h, path);
    const { pools, poolIndex } = resolvePools(doc);
    return { name: doc.name, path, doc, orient, kinds: kindGrid(doc, kindForClass, Kind.OPEN), poolIndex, pools };
  });
}

/**
 * Every authored tile the build has, parsed and resolved once.
 *
 * Lazy for the reason `rigidShapes.ts`'s `shapes()` is: a tool that only wants
 * the lattice should not pay to parse eight thousand characters of room.
 */
export function authoredTiles(): readonly AuthoredTile[] {
  if (cachedAuthored !== null) return cachedAuthored;
  cachedAuthored = tilesFrom(TILE_TEXT);
  return cachedAuthored;
}

/**
 * A pin document's slot key — `(ux, uy, orient)` in `TileOrientId` space, the
 * key `resolvePins`' map uses. Exported so a test can probe the map
 * `resolvePins` returns without knowing its internal shape any other way.
 */
export function pinnedSlotKey(ux: number, uy: number, orient: TileOrientId): string {
  return `${ux},${uy},${orient}`;
}

/**
 * Resolve a pin document's tile names against a tile registry.
 *
 * The refusal issue #182 asks for — *"an unknown name is a load-time
 * refusal, not a silent skip"* — happens here, once per document, in the
 * same shape `materials.ts`'s `finalizeMaterials` refuses a dangling
 * `shatteredInto` name: `` `<thing> "<name>" referenced but not defined` ``.
 * A pin whose declared orient does not match its named tile's own orient is
 * refused the same way — a pin can only ever address a slot its tile
 * actually fits, and a mismatch here is not "the wrong half of the tile", it
 * is a document naming a horizontal tile at a vertical slot or the reverse,
 * which no lattice geometry can honour.
 *
 * Exported and pure — takes the tile pool as an argument rather than calling
 * `authoredTiles()` itself, the same reason `resolvePools` takes a document
 * rather than reading one off a module cache — so a test can resolve a pin
 * document against a synthetic pool without touching the shipped tiles.
 */
export function resolvePins(doc: PinDoc, tiles: readonly AuthoredTile[]): ReadonlyMap<string, AuthoredTile> {
  const byName = new Map<string, AuthoredTile>();
  for (const t of tiles) if (!byName.has(t.name)) byName.set(t.name, t);
  const out = new Map<string, AuthoredTile>();
  for (const p of doc.pins) {
    const tile = byName.get(p.tileName);
    if (tile === undefined) {
      throw new Error(`worldmap: tile "${p.tileName}" referenced but not defined`);
    }
    const wantOrient: TileOrientId = p.orient === 'H' ? TileOrient.H : TileOrient.V;
    if (tile.orient !== wantOrient) {
      const gotOrient = tile.orient === TileOrient.H ? 'H' : 'V';
      throw new Error(
        `worldmap: pin (${p.ux}, ${p.uy}, ${p.orient}) names "${p.tileName}", which is a ` +
          `${gotOrient} tile — a pin's orient must match the tile it names`,
      );
    }
    out.set(pinnedSlotKey(p.ux, p.uy, wantOrient), tile);
  }
  return out;
}

let cachedPinnedTiles: ReadonlyMap<string, AuthoredTile> | null = null;

/**
 * The shipped pin document, parsed and resolved once — `authoredTileAt`'s
 * default. Lazy for the reason `authoredTiles` is; empty in this build
 * (`src/world/worldmap.pxm` ships with no pins), so every slot falls through
 * to the hash path unchanged and today's worlds reproduce byte for byte —
 * the fallback guarantee issue #182 asks for, held by construction rather
 * than a special case.
 */
function shippedPinnedTiles(): ReadonlyMap<string, AuthoredTile> {
  if (cachedPinnedTiles !== null) return cachedPinnedTiles;
  const doc = parsePinDoc(WORLDMAP_TEXT, 'src/world/worldmap.pxm');
  cachedPinnedTiles = resolvePins(doc, authoredTiles());
  return cachedPinnedTiles;
}

/**
 * Test-only override of what `authoredTileAt`'s default pin resolution
 * answers, so a test can exercise the pin path through the real
 * `buildTile`/`WorldGen` call chain without editing the shipped
 * `worldmap.pxm` — the same reason `tilesFrom` exists next to
 * `authoredTiles`, one level down: a pure function to resolve a synthetic
 * document against, with a seam to install the result as the default. Pass
 * `null` to drop back to the shipped (empty) document.
 */
export function __setPinnedTilesForTest(pinned: ReadonlyMap<string, AuthoredTile> | null): void {
  cachedPinnedTiles = pinned;
}

/**
 * **Which authored room this slot is, or `null` for the procedural one.**
 *
 * A pure function of the slot's own identity and the world seed, and it draws
 * from no `Rng`. That is the determinism statement this feature owes, and it is
 * two clauses: *the choice* is seeded — same seed, same slots, and a chunk
 * regenerating after eviction picks the same room without asking anyone — and
 * *the room itself* is a constant, the same eight thousand characters in every
 * world at every seed. **Authored stops at the file; seeded begins at which
 * slot gets it.** Nothing in between varies *geometrically*: there is no
 * per-placement mirror, rotation or geometric re-roll, deliberately, because
 * each of those is a second description of the room and `docs/19` §3.4
 * records that Noita's own per-placement variation is a *material* re-roll
 * rather than a geometric one. That material half is now built — a tile may
 * declare a legend pool (`LegendPool` in `src/sim/shapeMask.ts`), resolved
 * once per pool per placement in `stampAuthored`, issue #183 — and it changes
 * nothing said here: the grid stays the constant this docblock describes.
 *
 * `tileSeed` is `ctx.seed` — `hash2(ux, uy, seed ^ orientSalt)` — so a tile
 * straddling two chunks answers the same from both.
 *
 * The pool is **not** filtered by biome, which is where this differs from Noita
 * (their pools are per-biome, `docs/19` §6). It is the claim under test: a room
 * drawn entirely in deferred classes has no biome in it, so one tile should
 * drop into twelve and read as native in each. `pixel tile show` renders it in
 * two of them side by side, which is what that claim looks like when it is
 * checkable rather than asserted.
 *
 * **A pin (issue #182) is checked first, and wins unconditionally.** A pin
 * document maps a slot's own identity — the same `(ux, uy, orient)` this
 * function already takes, nothing new to address by — straight to a tile,
 * with no `AUTHORED_PERIOD` gate and no hash roll: if the slot is pinned,
 * that tile is the answer, full stop. That is *more* deterministic than the
 * hash path, not less — a pinned slot's tile does not depend on `tileSeed`
 * at all, so it agrees across every seed and (by the same argument the
 * paragraph above makes for the hash path) across a tile straddling two
 * chunks. Falling through to the hash path is exactly what an unpinned slot
 * always did, so an empty pin document — what ships today — changes nothing
 * here; `pinned` defaults to the shipped document and a caller only ever
 * supplies its own for a test, via `resolvePins`.
 */
export function authoredTileAt(
  ux: number,
  uy: number,
  orient: TileOrientId,
  tileSeed: number,
  pinned: ReadonlyMap<string, AuthoredTile> = shippedPinnedTiles(),
): AuthoredTile | null {
  const pin = pinned.get(pinnedSlotKey(ux, uy, orient));
  if (pin !== undefined) return pin;
  const pool = authoredTiles().filter((t) => t.orient === orient);
  if (pool.length === 0) return null;
  const h = hash2(ux, uy, tileSeed ^ 0xa47ed7);
  if (h % AUTHORED_PERIOD !== 0) return null;
  return pool[hash2(uy, ux, tileSeed ^ 0x5cede1) % pool.length];
}

/**
 * The salt a pool's roll is hashed under (issue #183). Distinct from every
 * other salt this file hashes a tile placement's `(ux, uy, seed)` under —
 * `0xa47ed7` (the authored-period gate) and `0x5cede1` (which room the pool
 * gives the slot) are the two at this same call site; the arena/corridor/etc.
 * dressing salts (`0x11`..`0x60d1e5`) hash a *different* triple, `(x, ctx.seed,
 * salt)` inside a room body that has already been chosen. None of them collide
 * with this one bit for bit, which is the whole of what a salt needs to do.
 */
const POOL_SALT = 0x2eed17;

/**
 * Choose one pool entry by weight. `roll` is already reduced mod
 * `p.totalWeight`, so this always returns before the loop runs out — the
 * fallback below is TypeScript's exhaustiveness requirement, not a case that
 * can happen.
 */
function pickWeighted(p: ResolvedPool, roll: number): KindId {
  let acc = 0;
  for (let k = 0; k < p.weights.length; k++) {
    acc += p.weights[k];
    if (roll < acc) return p.kinds[k];
  }
  return p.kinds[p.kinds.length - 1];
}

/**
 * Stamp an authored tile into the chunk.
 *
 * The same three rules `rect` obeys, because they are the tiler's rules and not
 * this stamp's: clipped to the chunk being generated, `PROTECT_MEGA` wins, and
 * every write is a pure function of world position so two chunks sharing a tile
 * agree on the overlap without talking.
 *
 * **The per-pool rolls (issue #183) are the same pure function of world
 * position.** One roll per pool per placement, not per cell — every cell
 * sharing a pool character in one placement resolves to the same chosen class,
 * which is what "the resolution of a legend class at stamp time" means. Rolled
 * once here, from `ctx.seed` — itself `hash2(ux, uy, worldSeed ^ orientSalt)`,
 * per `authoredTileAt`'s docblock — so the roll is a pure function of
 * `(ux, uy, worldSeed)` and two chunks sharing this tile agree on it without
 * talking, the same guarantee the rest of this function already gives the
 * geometry.
 */
function stampAuthored(ctx: TileCtx, tile: AuthoredTile): void {
  const { x, y } = ctx;
  const w = tile.doc.w;
  const kinds = tile.kinds;
  const x0 = x < ctx.baseX ? ctx.baseX : x;
  const y0 = y < ctx.baseY ? ctx.baseY : y;
  const xe = ctx.baseX + CHUNK_SIZE;
  const ye = ctx.baseY + CHUNK_SIZE;
  const x1 = x + w > xe ? xe : x + w;
  const y1 = y + tile.doc.h > ye ? ye : y + tile.doc.h;
  if (x1 <= x0 || y1 <= y0) return;
  const ux = x >> RU_SHIFT;
  const uy = y >> RU_SHIFT;
  const rolls: KindId[] = tile.pools.map((p, k) => {
    const roll = hash2(ux, uy, ctx.seed ^ POOL_SALT ^ k) % p.totalWeight;
    return pickWeighted(p, roll);
  });
  const poolIndex = tile.poolIndex;
  const kind = ctx.kind;
  const prot = ctx.protect;
  for (let wy = y0; wy < y1; wy++) {
    const row = ((wy - ctx.baseY) << CHUNK_SHIFT) - ctx.baseX;
    const src = (wy - y) * w - x;
    for (let wx = x0; wx < x1; wx++) {
      const i = row + wx;
      if (prot[i] >= PROTECT_MEGA) continue;
      const pi = poolIndex[src + wx];
      kind[i] = pi < 0 ? kinds[src + wx] : rolls[pi];
    }
  }
}

/**
 * Raise the suppression mask over what was just stamped — see `PROTECT_AUTHORED`.
 *
 * A separate pass, and **after `edgeWalls`**, because `rect` refuses to write at
 * or above `PROTECT_MEGA`: protecting during the stamp would lock the tile
 * against its own exterior walls, and the edges are the half of a tile that may
 * not be authored away.
 *
 * Cells already at `PROTECT_MEGA` or above keep what they have. A mega feature
 * outranks a room by construction — `docs/21` §2.5(d), the small thing does not
 * erase the large thing — and `stampAuthored` has already declined to write
 * `kind` there, so protecting them here would claim a room that is not on the
 * ground.
 */
function protectAuthored(ctx: TileCtx, tile: AuthoredTile): void {
  const { x, y } = ctx;
  const x0 = x < ctx.baseX ? ctx.baseX : x;
  const y0 = y < ctx.baseY ? ctx.baseY : y;
  const xe = ctx.baseX + CHUNK_SIZE;
  const ye = ctx.baseY + CHUNK_SIZE;
  const x1 = x + tile.doc.w > xe ? xe : x + tile.doc.w;
  const y1 = y + tile.doc.h > ye ? ye : y + tile.doc.h;
  const prot = ctx.protect;
  for (let wy = y0; wy < y1; wy++) {
    const row = ((wy - ctx.baseY) << CHUNK_SHIFT) - ctx.baseX;
    for (let wx = x0; wx < x1; wx++) {
      const i = row + wx;
      if (prot[i] >= PROTECT_MEGA) continue;
      prot[i] = PROTECT_AUTHORED;
    }
  }
}

const SHELL_T = 3;

/**
 * **The closest two one-way landings in a room may be, in cells.** Issue #57.
 *
 * `worldgen.ts` `SHELF_MIN_HEADROOM` is 25 — *"the 22-cell body plus the three
 * cells of deposit drift that settle on a floor"* — and it is the number every
 * other floor in the game is spaced by: the deck pitch is `25 + slab`, a
 * gallery is 25 tall by construction, and `ledges()` puts a landing inside a
 * mega void once per band pitch *"so the landing is under the 54-cell jump by
 * construction"*.
 *
 * The room shapes did not read that rule. `staggered` laid a landing every
 * **12** cells and `spiral` every **9**, and a `tiered` arena divided its
 * interior into four, which at an `RU` of 64 is a tier every **7**. A one-way
 * floor 9 cells under another one is not a floor: the 22-cell body does not fit
 * between them, so the player cannot stand on the lower one at all, and what is
 * drawn is a comb rather than architecture.
 *
 * It is also *area*, which is how it was found. `palette.platform` realises
 * 9.0-11.0 % of the solid area of every biome and clears
 * `tests/paletteStructure.test.ts`'s 5 % floor in **every window of every
 * biome** — the only material in the game of which that is true.
 *
 * 25 rather than a taste value because it is the number the rest of the
 * generator already spaces floors by, and because it is the smallest gap the
 * body fits through. The count falls out of it: a repeat is a *count* rather
 * than a literal pitch, so a room three times the size gets three times as many
 * landings rather than the same comb three times finer — which is also what
 * `docs/19` §8.1's `RU` of 192 needs, since a literal pitch drew a colonnade of
 * thirty-one pillars in a 300-cell room the last time that was attempted.
 *
 * **Honest size.** It is worth about 0.1 points of `palette.platform` area. The
 * rest of that 9-11 % is `worldgen.ts`'s shaft rungs and gallery crossings,
 * every one of which is pinned by a movement contract `tests/worldshape.test.ts`
 * holds — see the note over `OVER_SIMULTANEOUS` in
 * `tests/paletteStructure.test.ts`. This is the part that was not pinned by
 * anything, and it was a defect rather than a trade.
 */
const LANDING_MIN_GAP = 25;

/**
 * A repeated element's pitch, from a count rather than from a literal.
 *
 * `count` is what the shape means; `min` is the floor the body imposes. At the
 * authored `RU` of 64 the two agree with what was there before for every shape
 * except the three landing combs, which is what makes this a fix rather than a
 * retune.
 */
function pitchFor(span: number, count: number, min: number): number {
  if (count < 1) return span + 1;
  return Math.max(min, Math.floor(span / count));
}

/**
 * Walls are drawn PER EDGE, not as a ring.
 *
 * A ring around every tile is what makes a tiled world look tiled: you get a
 * closed box at every lattice position and the eye assembles them into a grid
 * even when the tiles interlock. Drawing a wall only where the edge field said
 * WALL means an OPEN edge genuinely merges two rooms into one space, a DOOR
 * edge gets a thinner partition the corridor punches through, and the boxes
 * dissolve.
 */
function edgeWalls(ctx: TileCtx, shellMat: number): void {
  const { x, y, w, h, edges: e } = ctx;
  const half = ctx.orient === TileOrient.H ? w >> 1 : h >> 1;
  const tOf = (edge: number): number => (edge === EdgeType.WALL ? SHELL_T : edge === EdgeType.DOOR ? 2 : 0);

  if (ctx.orient === TileOrient.H) {
    let t = tOf(e[0]); if (t) rect(ctx, x, y, t, h, shellMat);           // W
    t = tOf(e[1]); if (t) rect(ctx, x + w - t, y, t, h, shellMat);       // E
    t = tOf(e[2]); if (t) rect(ctx, x, y, half, t, shellMat);            // N over unit A
    t = tOf(e[3]); if (t) rect(ctx, x + half, y, half, t, shellMat);     // N over unit B
    t = tOf(e[4]); if (t) rect(ctx, x, y + h - t, half, t, shellMat);    // S over unit A
    t = tOf(e[5]); if (t) rect(ctx, x + half, y + h - t, half, t, shellMat);
  } else {
    let t = tOf(e[0]); if (t) rect(ctx, x, y, w, t, shellMat);           // N
    t = tOf(e[1]); if (t) rect(ctx, x, y + h - t, w, t, shellMat);       // S
    t = tOf(e[2]); if (t) rect(ctx, x, y, t, half, shellMat);            // W over unit A
    t = tOf(e[3]); if (t) rect(ctx, x, y + half, t, half, shellMat);     // W over unit B
    t = tOf(e[4]); if (t) rect(ctx, x + w - t, y, t, half, shellMat);    // E over unit A
    t = tOf(e[5]); if (t) rect(ctx, x + w - t, y + half, t, half, shellMat);
  }
}

/**
 * Write a tile's room body into the chunk. Pure function of the ctx — two
 * chunks sharing a tile produce byte-identical results for the overlap.
 */
export function buildTile(ctx: TileCtx): void {
  const { x, y, w, h } = ctx;
  const shellMat = ctx.variant.sealed ? Kind.GATE : Kind.SHELL;

  // ---- the authored substitute
  //
  // **An authored room replaces the body and never the edges**, and the order
  // of these two statements is the whole of why one drawn room is legal at
  // every edge signature. The stamp goes down first and `edgeWalls` goes over
  // it, so the six exterior edges are still exactly what the edge field dealt
  // — a WALL is three cells of shell whoever is inside, a DOOR is two, an OPEN
  // edge is nothing at all. No variant per signature, no filter, no search: the
  // drop-in property `rooms.ts`'s header claims for derived edge matching,
  // spent.
  //
  // A sealed slot is exempt. Its walls are ADAMANT_SEAL and `docs/01` §3.4's
  // honest gating means the matching ability is the only way in; stamping a
  // room with an open ceiling into one would unseal it, and it is cheaper to
  // say so here than to require every authored tile to know about vaults.
  //
  // No size check here, and the absence is deliberate: `authoredTiles()` refuses
  // at load any document that is not `2*RU x RU` or `RU x 2*RU`, and derives the
  // tile's orientation from which one it is, so a tile handed to a slot of this
  // orientation already has this slot's dimensions. A second check would be a
  // branch nothing can take — found by mutation, which is what a redundant guard
  // looks like from the outside.
  if (!ctx.variant.sealed) {
    const authored = authoredTileAt(x >> RU_SHIFT, y >> RU_SHIFT, ctx.orient, ctx.seed);
    if (authored !== null) {
      stampAuthored(ctx, authored);
      edgeWalls(ctx, shellMat);
      protectAuthored(ctx, authored);
      return;
    }
  }

  edgeWalls(ctx, shellMat);

  // The room body deliberately does NOT fill its tile. The margin is where the
  // cave noise survives, so rooms sit inside rock instead of tiling the world
  // into a continuous open box.
  const size = 0.55 + ctx.biome.terrain.roomSizeBias * 0.45;
  // Was 0.45 + bias*0.28. The router now carves galleries a full shelf period
  // tall, so the tile bodies no longer have to supply the world's open volume
  // — and a room that fills three quarters of its tile leaves no rock between
  // itself and the next one, which is what made the world read as one cavern.
  const body = 0.30 + ctx.biome.terrain.roomSizeBias * 0.18;
  const iw = Math.max(6, (w * body) | 0);
  const ih = Math.max(6, (h * body) | 0);
  const ix = x + ((w - iw) >> 1);
  const iy = y + ((h - ih) >> 1);
  if (iw <= 2 || ih <= 2) return;

  const cx = ix + (iw >> 1);
  const cy = iy + (ih >> 1);

  switch (ctx.variant.archetype) {
    case 'arena':
      buildArena(ctx, ix, iy, iw, ih, cx, cy, size);
      break;
    case 'corridor':
      buildCorridor(ctx, ix, iy, iw, ih, cx, cy, size);
      break;
    case 'shaft':
      buildShaft(ctx, ix, iy, iw, ih, cx, cy, size);
      break;
    case 'vault':
      buildVault(ctx, ix, iy, iw, ih, cx, cy, size);
      break;
    default:
      buildCache(ctx, ix, iy, iw, ih, cx, cy, size);
      break;
  }

  dress(ctx, ix, iy, iw, ih);
}

function buildArena(
  ctx: TileCtx, ix: number, iy: number, iw: number, ih: number,
  cx: number, cy: number, size: number,
): void {
  const rx = Math.max(4, (iw * 0.5 * size) | 0);
  const ry = Math.max(4, (ih * 0.5 * size) | 0);
  switch (ctx.variant.shape) {
    case 'pillared':
      rect(ctx, ix, iy, iw, ih, Kind.OPEN);
      // Pillars are SHELL: destructible cover that still reads as architecture.
      for (let px = ix + 8; px < ix + iw - 6; px += 14) {
        if (hash2(px, ctx.seed, 0x11) % 4 === 0) continue;
        rect(ctx, px, iy + 2, 4, ih - 4, Kind.SHELL);
      }
      break;
    case 'tiered': {
      rect(ctx, ix, iy, iw, ih, Kind.OPEN);
      // As many tiers as the body fits between, not three whatever the height:
      // at `RU` = 64 an arena interior is 29 cells tall and three tiers put one
      // every seven, which the player cannot stand between.
      const tiers = Math.max(1, Math.floor(ih / LANDING_MIN_GAP) - 1);
      for (let t = 1; t <= tiers; t++) {
        const ly = iy + ((ih * t / (tiers + 1)) | 0);
        const off = (hash2(t, ctx.seed, 0x22) % (iw >> 1)) | 0;
        // One-way. A tiered arena is only a tiered arena if you can climb it
        // and drop back through it; as solid SHELL the tiers were three
        // ceilings stacked over the fight.
        rect(ctx, ix + off, ly, (iw * 0.55) | 0, 3, Kind.PLATFORM);
      }
      break;
    }
    case 'pit':
      rect(ctx, ix, iy, iw, (ih * 0.45) | 0, Kind.OPEN);
      ellipse(ctx, cx, iy + ih - (ih >> 2), (iw * 0.4) | 0, (ih * 0.4) | 0, Kind.OPEN);
      rect(ctx, ix, iy + ((ih * 0.45) | 0), iw, 3, Kind.SHELL);
      // The lip is what makes a pit a decision: you can drop in, not walk out.
      rect(ctx, cx - 6, iy + ((ih * 0.45) | 0), 12, 3, Kind.OPEN);
      break;
    default: // bowl
      ellipse(ctx, cx, cy, rx, ry, Kind.OPEN);
      rect(ctx, cx - rx, cy + (ry >> 1), rx * 2, ry >> 1, Kind.OPEN);
      break;
  }
}

function buildCorridor(
  ctx: TileCtx, ix: number, iy: number, iw: number, ih: number,
  cx: number, cy: number, size: number,
): void {
  const band = Math.max(8, (ih * 0.55 * size) | 0);
  switch (ctx.variant.shape) {
    case 'colonnade':
      rect(ctx, ix, cy - (band >> 1), iw, band, Kind.OPEN);
      for (let px = ix + 6; px < ix + iw - 4; px += 11) {
        rect(ctx, px, cy - (band >> 1), 3, band, Kind.SHELL);
        rect(ctx, px, cy + (band >> 1) - 4, 3, 4, Kind.OPEN);
      }
      break;
    case 'zigzag': {
      const steps = 4;
      const sw = (iw / steps) | 0;
      for (let s = 0; s < steps; s++) {
        const dy = ((hash2(s, ctx.seed, 0x33) % (ih - band)) | 0) - ((ih - band) >> 1);
        rect(ctx, ix + s * sw, cy + dy - (band >> 1), sw + 2, band, Kind.OPEN);
      }
      break;
    }
    case 'ribbed':
      rect(ctx, ix, cy - (band >> 1), iw, band, Kind.OPEN);
      for (let px = ix + 4; px < ix + iw - 3; px += 9) {
        rect(ctx, px, cy - (band >> 1) - 2, 3, 3, Kind.SHELL);
        rect(ctx, px, cy + (band >> 1) - 1, 3, 3, Kind.SHELL);
      }
      break;
    default: // straight
      rect(ctx, ix, cy - (band >> 1), iw, band, Kind.OPEN);
      // Alcoves give the gallery somewhere to hide loot and enemies.
      for (let px = ix + 10; px < ix + iw - 10; px += 18) {
        if (hash2(px, ctx.seed, 0x44) % 3 !== 0) continue;
        rect(ctx, px, cy + (band >> 1), 8, 6, Kind.OPEN);
      }
      break;
  }
}

function buildShaft(
  ctx: TileCtx, ix: number, iy: number, iw: number, ih: number,
  cx: number, cy: number, size: number,
): void {
  const band = Math.max(8, (iw * 0.55 * size) | 0);
  switch (ctx.variant.shape) {
    case 'staggered': {
      rect(ctx, cx - (band >> 1), iy, band, ih, Kind.OPEN);
      let side = 0;
      // Four landings in the authored 58-cell shaft, and never closer than the
      // body is tall. See `LANDING_MIN_GAP`.
      const step = pitchFor(ih - 14, 4, LANDING_MIN_GAP);
      for (let py = iy + 8; py < iy + ih - 6; py += step) {
        const lw = (band * 0.6) | 0;
        // Landings, not shelves: a shaft is descended by dropping through and
        // ascended by jumping up through the same cells.
        rect(ctx, side ? cx - (band >> 1) : cx + (band >> 1) - lw, py, lw, 3, Kind.PLATFORM);
        side ^= 1;
      }
      break;
    }
    case 'spiral': {
      rect(ctx, cx - (band >> 1), iy, band, ih, Kind.OPEN);
      let t = 0;
      const step = pitchFor(ih - 11, 6, LANDING_MIN_GAP);
      for (let py = iy + 6; py < iy + ih - 5; py += step) {
        const off = Math.round(Math.cos(t) * (band * 0.35));
        rect(ctx, cx + off - 5, py, 10, 3, Kind.PLATFORM);
        t += 0.9;
      }
      break;
    }
    case 'chimney': {
      // Pinches in the middle — Updraft/Cling territory.
      const top = (ih * 0.4) | 0;
      rect(ctx, cx - (band >> 1), iy, band, top, Kind.OPEN);
      rect(ctx, cx - 4, iy + top, 8, (ih * 0.2) | 0, Kind.OPEN);
      rect(ctx, cx - (band >> 1), iy + top + ((ih * 0.2) | 0), band, ih - top - ((ih * 0.2) | 0), Kind.OPEN);
      break;
    }
    default: // plain
      rect(ctx, cx - (band >> 1), iy, band, ih, Kind.OPEN);
      for (let py = iy + 14; py < iy + ih - 8; py += 20) {
        if (hash2(py, ctx.seed, 0x55) % 2 !== 0) continue;
        rect(ctx, cx + (band >> 1), py, 7, 6, Kind.OPEN);
      }
      break;
  }
}

function buildVault(
  ctx: TileCtx, ix: number, iy: number, iw: number, ih: number,
  cx: number, cy: number, size: number,
): void {
  // The shell is already ADAMANT_SEAL (ctx.variant.sealed). No doorway is cut:
  // the matching traversal ability is the only way in, which is what docs/01
  // §3.4 means by honest gating — if you can melt it, it was never a gate.
  switch (ctx.variant.shape) {
    case 'reliquary':
      rect(ctx, ix, iy, iw, ih, Kind.OPEN);
      rect(ctx, cx - 4, cy - 3, 8, ih - (cy - iy) + 3, Kind.SHELL);
      break;
    case 'oubliette':
      rect(ctx, ix + (iw >> 2), iy, iw >> 1, ih, Kind.OPEN);
      break;
    case 'magazine':
      for (let py = iy; py < iy + ih - 6; py += 10) {
        rect(ctx, ix, py, iw, 6, Kind.OPEN);
      }
      rect(ctx, cx - 3, iy, 6, ih, Kind.OPEN);
      break;
    default: // strongroom
      rect(ctx, ix, iy, iw, ih, Kind.OPEN);
      break;
  }
}

function buildCache(
  ctx: TileCtx, ix: number, iy: number, iw: number, ih: number,
  cx: number, cy: number, size: number,
): void {
  const rx = Math.max(4, (iw * 0.3 * size) | 0);
  const ry = Math.max(4, (ih * 0.3 * size) | 0);
  switch (ctx.variant.shape) {
    case 'pocket':
      ellipse(ctx, cx, cy, rx, ry, Kind.OPEN);
      break;
    case 'cellar':
      rect(ctx, cx - rx, cy, rx * 2, ry, Kind.OPEN);
      rect(ctx, cx - rx, cy + ry, rx * 2, 3, Kind.SHELL);
      break;
    case 'shrine':
      ellipse(ctx, cx, cy, rx, ry, Kind.OPEN);
      rect(ctx, cx - 2, cy - 1, 4, ry, Kind.SHELL);
      break;
    default: // niche
      rect(ctx, cx - rx, cy - ry, rx * 2, ry * 2, Kind.OPEN);
      break;
  }
}

/**
 * Dressing pass — the third variant axis. Never closes a room off; it only
 * adds SOLID nubs inside open space, so it can't invalidate connectivity.
 */
function dress(ctx: TileCtx, ix: number, iy: number, iw: number, ih: number): void {
  const d = ctx.variant.dressing;
  if (d === 'bare') return;
  const kind = ctx.kind;
  const prot = ctx.protect;
  const x0 = Math.max(ctx.baseX, ix);
  const y0 = Math.max(ctx.baseY, iy);
  const x1 = Math.min(ctx.baseX + CHUNK_SIZE, ix + iw);
  const y1 = Math.min(ctx.baseY + CHUNK_SIZE, iy + ih);
  const rubble = d === 'rubble';
  for (let wy = y0; wy < y1; wy++) {
    const row = ((wy - ctx.baseY) << CHUNK_SHIFT) - ctx.baseX;
    for (let wx = x0; wx < x1; wx++) {
      const i = row + wx;
      if (kind[i] !== Kind.OPEN || prot[i] >= PROTECT_MEGA) continue;
      if (rubble) {
        // Rubble clings to floors: only place where there is support below.
        //
        // Keyed on a 64-cell span, not on the cell. A per-cell roll put a
        // one-cell nub every sixty-four cells of floor, and since a nub is a
        // step it capped the longest level run in any dressed room at the
        // distance between two nubs. A bank of rubble is the same material,
        // the same density and a floor you can fight on.
        if (hash2(wx >> 6, wy, ctx.seed ^ 0x60d1e5) % 5 !== 0) continue;
        const below = i + CHUNK_SIZE;
        if (below >= kind.length || kind[below] === Kind.OPEN) continue;
        kind[i] = Kind.SOLID;
      } else {
        const h = hash2(wx, wy, ctx.seed ^ 0x60d1e5);
        // Overgrowth clings to walls.
        if ((h & 31) !== 0) continue;
        const left = wx > ctx.baseX ? kind[i - 1] : Kind.SOLID;
        const right = wx < ctx.baseX + CHUNK_SIZE - 1 ? kind[i + 1] : Kind.SOLID;
        if (left === Kind.OPEN && right === Kind.OPEN) continue;
        kind[i] = Kind.SOLID;
      }
    }
  }
}
